import { Background, BackgroundColor } from "../components/layout"
import { Color, TypographyColor } from "../components/typography"
import ImageEditor, { AspectRatio } from "../Image"
import { Text, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../Typography"

import React from "react"
import { useLocalesState } from "../../shared/context/locales"
import { useTranslation } from "react-i18next"

interface HeroArticleProps {
    badge: string
    headline: string
    text: TypographyElement[]
    author: string
    updatedAt: string
    readingTime: number
}

const HeroArticle: types.Brick<HeroArticleProps> = ({ author, updatedAt, readingTime }) => {
    const { t } = useTranslation()
    const { language } = useLocalesState()

    return (
        <Background color={BackgroundColor.DarkBlumine}>
            <div className="mx-auto box-border w-full pt-10 sm:grid sm:grid-cols-[auto_327px] sm:grid-rows-[auto_1fr] sm:gap-x-10 sm:px-6.25 sm:py-10 md:grid-cols-[auto_418px] md:grid-rows-[1fr_auto_auto_1fr] md:px-10 md:py-16 lg:max-w-[1560px] lg:grid-cols-[auto_555px] lg:px-15 xl:grid-cols-[auto_636px] xl:gap-x-16">
                <div />
                <div className="md-row-start-2 px-6.25 sm:col-span-2 sm:flex sm:flex-col sm:items-center sm:px-0 sm:text-center md:col-span-1 md:block md:text-left">
                    <div className="mb-3.5 flex lg:mb-6 xl:mb-5">
                        <Text
                            propName="badge"
                            renderBlock={({ children }) => (
                                <div className="text-box rounded-full bg-copperfield px-5 py-2 text-md leading-none font-semibold text-white lg:px-6 lg:py-3 lg:text-lg lg:leading-none">
                                    {children}
                                </div>
                            )}
                        />
                    </div>
                    <Color color={TypographyColor.White}>
                        <Text
                            propName="headline"
                            renderBlock={({ children }) => (
                                <h1 className="mb-5 font-gibson text-3xl font-semibold sm:mb-7 md:mb-5 lg:mb-7 lg:text-[50px]/[56px]">
                                    {children}
                                </h1>
                            )}
                        />
                    </Color>
                </div>
                <div className="col-start-1 flex items-center px-6.25 pb-10 sm:px-0 sm:pb-0 md:row-start-3">
                    <Color color={TypographyColor.White}>
                        <Typography block="copytext-article" allowList={["sup", "link", "bold"]} propName="text" />
                        <div className="mt-7 flex items-center lg:mt-10">
                            <ImageEditor
                                aspectRatio={AspectRatio["1/1"]}
                                maxWidth={40}
                                className="mr-3.5 h-[35px] w-[35px] self-start rounded-full xs:self-center lg:h-10 lg:w-10"
                                propName="authorImage"
                            />
                            <div className="flex flex-col gap-x-2 gap-y-1.5 lg:flex-row lg:items-center lg:gap-x-3.5">
                                <p className="text-sm after:h-1 after:w-1 after:rounded-full after:bg-white lg:text-base">
                                    {t("hero_article_author", { author })}
                                </p>
                                <div className="hidden h-1 w-1 rounded-full bg-white lg:block" />
                                <div className="flex flex-col gap-x-2 gap-y-1.5 xs:flex-row xs:items-center lg:gap-x-3.5">
                                    <p className="text-sm after:h-1 after:w-1 after:rounded-full after:bg-white lg:text-base">
                                        {t("hero_article_updated_at", {
                                            date: new Date(updatedAt).toLocaleDateString(language, {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                            }),
                                        })}
                                    </p>
                                    <div className="hidden h-1 w-1 rounded-full bg-white xs:block" />
                                    <p className="text-sm lg:text-base">
                                        {t("hero_article_reading_time", { duration: readingTime })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Color>
                </div>
                <ImageEditor
                    aspectRatio={AspectRatio["5/4"]}
                    maxWidth={717}
                    className="w-full sm:rounded-base md:col-start-2 md:row-span-4 md:row-start-1"
                    propName="image"
                />
            </div>
        </Background>
    )
}

HeroArticle.schema = {
    name: "HeroArticle",
    label: "Hero (article)",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/HeroArticle.png`,
    sideEditProps: [
        {
            name: "author",
            label: "Author",
            type: types.SideEditPropType.Text,
        },
        {
            name: "updatedAt",
            label: "Updated At",
            type: types.SideEditPropType.Date,
        },
        {
            name: "readingTime",
            label: "Reading Time",
            type: types.SideEditPropType.Number,
        },
    ],
    getDefaultProps: (): HeroArticleProps => ({
        badge: "Lorem ipsum",
        headline: "Lorem ipsum dolor sit amet",
        text: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
                    },
                ],
            },
        ],
        author: "Max Mustermann",
        updatedAt: "01.01.2024",
        readingTime: 5,
    }),
}

export default HeroArticle
